import React from "react";
import { Button, Text } from "components/commons";
import { formatAmount } from "utils";
import { formatPeriodFromApi } from "./single-invoicing.utils";
import styles from "./single-invoicing-history.module.scss";

const WITH_CHANGES_STATUS = "with-changes";

const STATUS_COLORS = {
  "with-changes": "#FCA630",
  "with changes": "#FCA630",
  accurate: "#00933D",
  archived: "#97A4AD",
};

const getStatusColor = (status = "") => {
  const normalizedStatus = `${status}`.trim().toLowerCase();
  return STATUS_COLORS[normalizedStatus] || undefined;
};

const toTitleCase = (value = "") => {
  return `${value}`
	.replace(/[-_]/g, " ")
	.split(" ")
	.filter(Boolean)
	.map((token) => `${token.charAt(0).toUpperCase()}${token.slice(1).toLowerCase()}`)
	.join(" ");
};

const isArchivedByDate = (dateString) => {
	if (!dateString) return false;

	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return false;

	date.setUTCHours(0, 0, 0, 0);

	const cutoff = new Date();
	cutoff.setUTCMonth(cutoff.getUTCMonth() - 6);
	cutoff.setUTCHours(0, 0, 0, 0);

	return date <= cutoff;
};

const isWithChangesStatus = (status = "") => {
  const normalizedStatus = `${status}`.trim().toLowerCase();
  return normalizedStatus === WITH_CHANGES_STATUS || normalizedStatus === "with changes";
};

const formatInvoicePeriod = (periodStart, periodEnd) => {
  if (!periodStart || !periodEnd) {
	return "-";
  }

  const startDate = new Date(periodStart);
  const endDate = new Date(periodEnd);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
	return "-";
  }

  const formatShortDate = (dateValue) => {
	return dateValue.toLocaleDateString("en-US", {
	  month: "short",
	  day: "2-digit",
	});
  };

  return `${formatShortDate(startDate)} to ${formatShortDate(endDate)}`;
};

const formatAmountDueDisplay = (item = {}) => {
  const totalAmountDue = formatAmount(item?.totalAmountDue || 0);

  if (!isWithChangesStatus(item?.status)) {
	return totalAmountDue;
  }

  return (
	<div className={styles.amountDueWrapper}>
	  <Text>{totalAmountDue}</Text>
	  <Text className={styles.previousAmountDue}>{formatAmount(item?.previousAmountDue || 0)}</Text>
	</div>
  );
};

export const mapDataToList = (invoiceGuide = {}, onViewInvoiceGuide, viewingInvoiceId) => {
  const effectiveStatus = isArchivedByDate(invoiceGuide?.periodEnd) ? "archived" : invoiceGuide?.status;
  return {
	businessName: invoiceGuide?.businessName || "-",
	invoicePeriod: formatInvoicePeriod(invoiceGuide?.periodStart, invoiceGuide?.periodEnd),
	company: invoiceGuide?.company || "-",
	product: toTitleCase(invoiceGuide?.product || ""),
	amountDue: formatAmountDueDisplay(invoiceGuide),
	status: (
		<span style={{ backgroundColor: getStatusColor(effectiveStatus), color: "#fff", padding: "2px 10px", borderRadius: "12px", display: "inline-block", fontWeight: 500, fontSize: "12px" }}>{toTitleCase(effectiveStatus || "")}</span>
	),
	action: (
	  <Button
		textButton
		onClick={() => {
		  onViewInvoiceGuide?.(invoiceGuide);
		}}
		loading={viewingInvoiceId === invoiceGuide?.singleInvoiceId}
	  >
		View
	  </Button>
	),
  };
};

export const mapFilterToRequest = (filterState = {}) => {
  const { invoiceMonth, invoiceYear, ...rest } = filterState;
  return {
	...rest,
	month: Number(invoiceMonth),
	year: Number(invoiceYear),
  };
};

export const apiMapper = (param = {}) => {
  return { ...param };
};

export const mapInvoiceGuideView = (invoiceGuide = {}, fallbackGuide = {}) => {
  const periodStart = invoiceGuide?.periodStart || fallbackGuide?.periodStart;
  const periodEnd = invoiceGuide?.periodEnd || fallbackGuide?.periodEnd;

  return {
	soldTo: invoiceGuide?.soldTo || fallbackGuide?.businessName || "-",
	date: formatPeriodFromApi(periodStart, periodEnd),
	address: invoiceGuide?.address || "-",
	tin: invoiceGuide?.tin || "-",
	totalLiters: invoiceGuide?.totalLiters || 0,
	fuelPurchased: invoiceGuide?.fuelPurchased || 0,
	discounts: invoiceGuide?.discounts || 0,
	plbCreditAmount: invoiceGuide?.plbCreditAmount || 0,
	vatableSales: invoiceGuide?.vatableSales || 0,
	vatAmount: invoiceGuide?.vatAmount || 0,
	totalSalesVat: invoiceGuide?.totalSalesVat || 0,
	lessVat: invoiceGuide?.lessVat || 0,
	amountNetOfVat: invoiceGuide?.amountNetOfVat || 0,
	addVat: invoiceGuide?.addVat || 0,
	totalAmountDue: invoiceGuide?.totalAmountDue || fallbackGuide?.totalAmountDue || 0,
  };
};

export const singleInvoicingHistoryColumns = [
  { text: "Business Name", key: "businessName" },
  { text: "Invoice Period", key: "invoicePeriod" },
  { text: "Company", key: "company" },
  { text: "Product", key: "product" },
  { text: "Amount Due/\nPrevious Amount Due", key: "amountDue" },
  { text: "Status", key: "status" },
  { text: "Action", key: "action" },
];

